import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { PageHeader, Button, Card, Table, Badge, Modal, FormField, Input, Select, statusBadge } from '../ui';
import { Plus, Users, ChevronRight, Shield } from 'lucide-react';

const POSITIONS = ['GK','DF','MF','FW'];

export default function Teams() {
  const { teams, players, addTeam } = useApp();
  const { isAdmin } = useAuth();
  const [selectedTeam, setSelectedTeam] = useState(teams[0] || null);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: '', ageGroup: 'U12', city: '', color: 'var(--gold-main)' });
  const [playerForm, setPlayerForm] = useState({ name: '', position: 'MF', number: '', dob: '' });

  const teamPlayers = selectedTeam ? players.filter(p => selectedTeam.playerIds?.includes(p.id)) : [];

  const submitTeam = (e) => {
    e.preventDefault();
    addTeam({ ...teamForm, abbr: teamForm.name.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,3), playerIds: [] });
    setShowAddTeam(false);
    setTeamForm({ name: '', ageGroup: 'U12', city: '', color: 'var(--gold-main)' });
  };

  const waiverCount = teamPlayers.filter(p => p.waiver).length;
  const medicalCount = teamPlayers.filter(p => p.medical).length;

  return (
    <div>
      <PageHeader title="Teams & Rosters" subtitle={`${teams.length} teams · ${players.length} players`}
        actions={isAdmin && <Button variant="primary" onClick={() => setShowAddTeam(true)}><Plus size={15} /> Add team</Button>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '1.25rem', alignItems: 'start' }}>
        {/* Team list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {teams.map(team => {
            const tp = players.filter(p => team.playerIds?.includes(p.id));
            const isSelected = selectedTeam?.id === team.id;
            return (
              <div key={team.id} onClick={() => setSelectedTeam(team)}
                style={{ padding: '12px 14px', background: isSelected ? 'var(--surface-0)' : 'var(--surface-0)', border: `1.5px solid ${isSelected ? team.color : 'var(--border)'}`, borderRadius: 12, cursor: 'pointer', transition: 'all 0.15s', boxShadow: isSelected ? `0 0 0 3px ${team.color}22` : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: team.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-condensed)', fontWeight: 800, color: '#fff', fontSize: 12 }}>{team.abbr}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{team.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{team.ageGroup} · {tp.length} players</div>
                  </div>
                  <ChevronRight size={14} color="var(--text-muted)" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Roster detail */}
        {selectedTeam ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Team header */}
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: '1rem' }}>
                <div style={{ width: 56, height: 56, borderRadius: 14, background: selectedTeam.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-condensed)', fontWeight: 800, color: '#fff', fontSize: 18 }}>{selectedTeam.abbr}</div>
                <div style={{ flex: 1 }}>
                  <h2 style={{ fontSize: 20, fontWeight: 800, fontFamily: 'var(--font-condensed)', color: 'var(--text-primary)' }}>{selectedTeam.name}</h2>
                  <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{selectedTeam.ageGroup} · {selectedTeam.city}</p>
                </div>
                {isAdmin && <Button variant="secondary" size="sm" onClick={() => setShowAddPlayer(true)}><Plus size={13} /> Add player</Button>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                {[
                  { label: 'Players', value: teamPlayers.length, color: 'var(--gold-main)' },
                  { label: 'Waivers', value: `${waiverCount}/${teamPlayers.length}`, color: waiverCount === teamPlayers.length ? '#43a047' : '#f57c00' },
                  { label: 'Medical on file', value: `${medicalCount}/${teamPlayers.length}`, color: medicalCount === teamPlayers.length ? '#43a047' : '#f57c00' },
                  { label: 'Compliance', value: (waiverCount === teamPlayers.length && medicalCount === teamPlayers.length) ? '✓ OK' : '⚠ Issues', color: (waiverCount === teamPlayers.length && medicalCount === teamPlayers.length) ? '#43a047' : '#e53935' },
                ].map(item => (
                  <div key={item.label} style={{ textAlign: 'center', padding: '10px', background: 'var(--surface-1)', borderRadius: 10 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{item.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, fontFamily: 'var(--font-condensed)', color: item.color }}>{item.value}</div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Roster table */}
            <Card padding="0">
              <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Player roster ({teamPlayers.length})</h3>
                <div style={{ display: 'flex', gap: 6 }}>
                  {isAdmin && <Button variant="secondary" size="sm">Import CSV</Button>}
                  <Button variant="secondary" size="sm">Print roster</Button>
                </div>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: 'var(--surface-1)' }}>
                    {['#','Player','Position','DOB','Waiver','Medical',''].map(h => (
                      <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid var(--border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {teamPlayers.length === 0 ? (
                    <tr><td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No players on this roster yet</td></tr>
                  ) : teamPlayers.map(p => (
                    <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-1)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--text-muted)' }}>{p.number}</td>
                      <td style={{ padding: '10px 14px', fontWeight: 600 }}>{p.name}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ fontWeight: 700, fontSize: 12, color: { GK:'var(--gold-deep)', DF:'#2e7d32', MF:'#f57f17', FW:'#c62828' }[p.position] || '#333' }}>{p.position}</span>
                      </td>
                      <td style={{ padding: '10px 14px', color: 'var(--text-secondary)', fontSize: 13 }}>{p.dob}</td>
                      <td style={{ padding: '10px 14px' }}>{p.waiver ? <Badge variant="green">Signed</Badge> : <Badge variant="red">Missing</Badge>}</td>
                      <td style={{ padding: '10px 14px' }}>{p.medical ? <Badge variant="green">On file</Badge> : <Badge variant="amber">Pending</Badge>}</td>
                      <td style={{ padding: '10px 14px' }}>
                        {isAdmin && <button style={{ fontSize: 12, color: 'var(--gold-main)', background: 'none', border: 'none', cursor: 'pointer' }}>Edit</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
        ) : (
          <Card><p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>Select a team to view roster</p></Card>
        )}
      </div>

      {/* Add Team Modal */}
      <Modal open={showAddTeam} onClose={() => setShowAddTeam(false)} title="Add new team">
        <form onSubmit={submitTeam} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <FormField label="Team name" required>
              <Input value={teamForm.name} onChange={e => setTeamForm(p => ({...p, name: e.target.value}))} required placeholder="e.g. Thunder FC" />
            </FormField>
            <FormField label="Age group" required>
              <Select value={teamForm.ageGroup} onChange={e => setTeamForm(p => ({...p, ageGroup: e.target.value}))}>
                {['U8','U10','U12','U14','U16','U19'].map(ag => <option key={ag}>{ag}</option>)}
              </Select>
            </FormField>
            <FormField label="City">
              <Input value={teamForm.city} onChange={e => setTeamForm(p => ({...p, city: e.target.value}))} placeholder="City" />
            </FormField>
            <FormField label="Team color">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="color" value={teamForm.color} onChange={e => setTeamForm(p => ({...p, color: e.target.value}))} style={{ width: 40, height: 36, border: '1px solid var(--border)', borderRadius: 8, padding: 2, cursor: 'pointer' }} />
                <Input value={teamForm.color} onChange={e => setTeamForm(p => ({...p, color: e.target.value}))} style={{ flex: 1 }} />
              </div>
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <Button type="button" variant="secondary" onClick={() => setShowAddTeam(false)}>Cancel</Button>
            <Button type="submit" variant="primary">Create team</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
